import { Hourglass, Umbrella, Vault } from '@phosphor-icons/react';

import pt from '../../locales/pt';
import { formatEUR } from '../../utils/money';
import { monthlySmoothingAmount, reservedPct } from '../../lib/annualCalendarUtils';
import { cn } from '../../utils/cn';

/**
 * `.pen` → `ds/tile/stat--hint` (master `po6U1`). Instâncias confirmadas nos
 * frames 375/768/1280: padding `space-2` (mobile) → `space-3` (tablet+),
 * ícone 12px (mobile) → 14px (tablet+). O valor mantém sempre `.type-display-stat`
 * (17px) — a variante mobile do `.pen` usa 15px (`.type-display-row`), diferença
 * de 2px aceite para não introduzir `md:type-*` (classes `.type-*` não são
 * utilities Tailwind, não podem ser prefixadas com variantes responsivas).
 */
const TILE_TONES = {
  warning: {
    wrap: 'bg-warning-tint border-border',
    text: 'text-warning-fg',
  },
  sage: {
    wrap: 'bg-sage-tint border-sage-border',
    text: 'text-sage-on-tint',
  },
  muted: {
    wrap: 'bg-muted border-border',
    text: 'text-muted-foreground',
  },
};

function PlanMetricTile({ tone, icon: Icon, label, value, hint, valueClassName }) {
  const t = TILE_TONES[tone];
  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 flex-col gap-space-1 rounded-ds-lg border p-space-2 md:p-space-3',
        t.wrap,
      )}
    >
      <div className="flex items-center justify-between gap-space-1">
        <p className={cn('type-chip', t.text)}>{label}</p>
        <Icon className={cn('h-3 w-3 shrink-0 md:h-3.5 md:w-3.5', t.text)} aria-hidden />
      </div>
      <p className={cn('type-display-stat', valueClassName || t.text)}>{value}</p>
      <p className={cn('type-caption', t.text)}>{hint}</p>
    </div>
  );
}

export function AnnualPlanCard({ data, className }) {
  if (!data) return null;

  const annualTotal = Number(data.annual_total || 0);
  const annualAccumulated = Number(data.annual_accumulated || 0);
  const annualRemaining = Math.max(
    Number(data.annual_remaining ?? annualTotal - annualAccumulated),
    0,
  );
  const monthly = monthlySmoothingAmount(annualTotal);
  const pct = reservedPct(annualAccumulated, annualTotal);
  const count = Number(data.occasional_count ?? 0);

  const headlineParts = pt.calendar.plan.headline.split('{annual}');
  const beforeAnnual = headlineParts[0] ?? '';
  const afterAnnualRaw = headlineParts[1] ?? '';
  const monthlyParts = afterAnnualRaw.split('{monthly}');
  const betweenAnnualAndMonthly = monthlyParts[0] ?? '';
  const afterMonthly = monthlyParts[1] ?? '';
  const plannedHint =
    count === 1
      ? pt.calendar.oneItem
      : (pt.calendar.plan.metricPlannedHint || pt.calendar.plan.metricYearHint).replace(
          '{count}',
          String(count),
        );
  const inPotsHint = (
    pt.calendar.plan.metricInPotsHint || pt.calendar.plan.metricReservedHint
  ).replace('{pct}', String(pct));

  return (
    <section
      className={cn(
        'rounded-ds-lg border border-border bg-card p-space-4 shadow-[0_6px_16px] shadow-glass-shadow md:p-space-5',
        className,
      )}
    >
      <div className="flex items-center gap-space-2 type-section text-warning-fg">
        <Umbrella className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>{pt.calendar.plan.eyebrow}</span>
      </div>

      <p className="type-display-stat mt-space-3 text-balance leading-snug text-foreground">
        {beforeAnnual}
        <span className="whitespace-nowrap text-warning-fg">{formatEUR(annualTotal)}</span>
        {betweenAnnualAndMonthly}
        <span className="whitespace-nowrap text-sage-on-tint">{formatEUR(monthly)}</span>
        {afterMonthly}
      </p>

      <div className="mt-space-3 flex gap-space-2">
        <PlanMetricTile
          tone="warning"
          icon={Umbrella}
          label={pt.calendar.plan.metricPlanned || pt.calendar.plan.metricYear}
          value={formatEUR(annualTotal)}
          hint={plannedHint}
        />
        <PlanMetricTile
          tone="sage"
          icon={Vault}
          label={pt.calendar.plan.metricInPots || pt.calendar.plan.metricReserved}
          value={formatEUR(annualAccumulated)}
          hint={inPotsHint}
        />
        <PlanMetricTile
          tone="muted"
          icon={Hourglass}
          label={pt.calendar.plan.metricRemaining || pt.calendar.annualRemaining}
          value={formatEUR(annualRemaining)}
          hint={pt.calendar.plan.metricRemainingHint}
          valueClassName="text-foreground"
        />
      </div>
    </section>
  );
}
